import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { NAV_ITEMS } from "@/data/nav";
import { useUser } from "@/context/UserContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const LOGO =
  "https://customer-assets-v7afamib.emergentagent.net/job_edju-command/artifacts/tmtoc27k_edju-cat-type-logo-png.png";

const ROUTE_THEME = [
  { match: (p) => p === "/", theme: "theme-command-centre" },
  { match: (p) => p.startsWith("/product"), theme: "theme-product-content" },
  { match: (p) => p.startsWith("/launch"), theme: "theme-launch-tracker" },
  { match: (p) => p.startsWith("/marketing"), theme: "theme-marketing" },
  { match: (p) => p.startsWith("/investors"), theme: "theme-investor-pipeline" },
  { match: (p) => p.startsWith("/finance"), theme: "theme-finance" },
  { match: (p) => p.startsWith("/decisions"), theme: "theme-decisions-risks" },
];

const themeForPath = (pathname) =>
  ROUTE_THEME.find((r) => r.match(pathname))?.theme || "theme-command-centre";

export const AppShell = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAdmin, can } = useUser();
  const { pathname } = useLocation();
  const mobileItems = NAV_ITEMS.filter(
    (i) => (!i.adminOnly || isAdmin) && (!i.requires || can(i.requires))
  );

  return (
    <div className={cn("flex h-screen overflow-hidden bg-[#F4F6FB]", themeForPath(pathname))}>
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />

      {/* Mobile navigation */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 border-r-0 bg-[#14245F] p-0 text-white">
          <div className="flex h-16 items-center border-b border-white/10 px-5">
            <img src={LOGO} alt="Edju Cat" className="h-7 w-auto" />
          </div>
          <nav className="space-y-1.5 p-3">
            {mobileItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold transition-colors",
                      isActive
                        ? "bg-white/10 text-white"
                        : "text-[#AEBEE8] hover:bg-white/5 hover:text-white"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={19} strokeWidth={2} className={isActive ? "text-[#67E8F9]" : ""} />
                      {item.label}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </SheetContent>
      </Sheet>

      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header onOpenMobileNav={() => setMobileOpen(true)} />
        <main className="edju-scroll relative flex-1 overflow-y-auto px-4 py-8 md:px-8 lg:px-10">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
